import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { useForm, Controller } from 'react-hook-form'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import Select from 'react-select'
import { selectDropdownOptions } from '../../../redux/dropdownSlice'
import { setChangeMade } from '../../../redux/paymentSchedulePanelSlice'
import { setModal } from '../../../redux/modalSlice'
import ManagerApprovalIcons from '../../../components/elements/ManagerApprovalIcons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'

export default function CreditPayment(props) {
  const { loanId, refreshPaymentSchedule, navigateSlider, currentDate, customerInfo, paymentMethodHelperText, loanInfo, creditPaymentFields } = props
  const requesting = useRef(false)
  const request = useRequest()
  const dispatch = useDispatch()
  const [responseData, setResponseData] = useState([])
  const { register, handleSubmit, control, watch } = useForm();
  const [saving, setSaving] = useState(false)
  const dropdownOptions = useSelector(selectDropdownOptions);
  const [totalAmount, setTotalAmount] = useState(0);
  const watchedFields = watch(['principal_amount', 'interest_amount', 'late_fee', 'nsf_fee', 'credit_reimbursement_amount', 'payment_method_dropdown_option_id', 'payment_date']);
  const paymentMethodACHId = dropdownOptions.find((o) => o?.code_key === "payment_method_ach")?.id;
  const loanStatusPaidOffId = dropdownOptions.find((o) => o?.code_key === "loan_status_paidoff")?.id;

  useEffect(() => {
    // Slice the array to include only elements up to the 5th index
    const slicedFields = Object.values(watchedFields).slice(0, 5);
    // Calculate the sum
    const sum = slicedFields.reduce((acc, cur) => acc + parseFloat(cur || 0), 0);
    setTotalAmount(sum);

    const hasChanged = Object.keys(watchedFields).some((key) => {
      return watchedFields[key] != undefined;
    });
    dispatch(setChangeMade(hasChanged))
  }, [watchedFields]);

  useEffect(() => {
    getPrincipalPaydownInfo(loanId)
  }, [])

  const getPrincipalPaydownInfo = async (loanId) => {
    if (requesting.current) return;
    requesting.current = true;
    try {
      let endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_schedule/credit_info`;
      // Check if the currentDate variable has a value and, if so, add it as a query parameter
      if (currentDate !== null) {
        endpoint += `?current_date=${currentDate}`;
      }
      request.setEndpoint(endpoint)
      const response = await request.get()
      setResponseData(response?.data?.data);
      requesting.current = false;
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
      requesting.current = false;
    }
  }

  const onSubmit = async (formData) => {
    const paymentMethodOption = formData.payment_method_dropdown_option_id.value;
    if (paymentMethodACHId == paymentMethodOption && customerInfo?.ach_revocation_active) {
        dispatch(
          setModal({
            is_active: true,
            type: 'allow_ach_payments_modal',
            options: { customerId: customerInfo.id, hasBkConfirmed: customerInfo?.has_bk_confirmed }
          })
        )
    } else {
      setSaving(true)
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_schedule/credit`)
      try {
        if (currentDate !== null) {
          formData['current_date'] = currentDate;
        }
        formData.payment_method_dropdown_option_id = formData.payment_method_dropdown_option_id.value;
        const response = await request.post(formData)
        if (response.status === 200 || response.status === 201) {
          setSaving(false)
          dispatch(setNotification({ type: 'success', body: '' }))
          navigateSlider("form_submit")
          refreshPaymentSchedule()
        } else {
          setSaving(false)
          dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        }
      } catch (error) {
        setSaving(false)
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'error', body: errorMessage }))
      }
    }
  };

  const checkChangesMade = () => {
    navigateSlider("");
  }

  return (
    <div className="pl-5 py-3">
      {responseData.length == 0 && <div className="is-italic" style={{ textAlign: "center", padding: "20px", color: "grey" }}>--- Loading ---</div>}

      {responseData.length != 0 && (
        <form onSubmit={handleSubmit(onSubmit)} id="credit_payment_form">
          <p className="title is-5">Credit {responseData?.is_manager_approval_required && (<ManagerApprovalIcons type="mgr_approval_status_required" />)}</p>
          <p className="subtitle is-6">{responseData?.is_manager_approval_required && (`All credits require manager approval before they will be valid.`)}
          {loanInfo.loan_status_dropdown_option_id == loanStatusPaidOffId && (
            <span><br />If you are trying to refund a Paid Off loan, follow existing spreadsheet process with Finance department.</span>
          )}
          </p>
          <table className="table is-narrow table-payment-schedule-panel">
            <thead>
              <tr>
                <th>
                  Payment Method <span
                    className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                    data-tooltip={responseData?.payment_method_reason ?? paymentMethodHelperText}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ zIndex: 15, position: 'relative' }}>
                  <Controller
                    name="payment_method_dropdown_option_id"
                    defaultValue={dropdownOptions.filter((o) => responseData?.default_payment_method_dropdown_option_id == o.id).map((o) => ({ label: o.name, value: o.id }))[0]}
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        {...register('payment_method_dropdown_option_id')}
                        options={dropdownOptions.filter((o) => responseData?.payment_method_dropdown_option_ids.includes(o.id)).map((o) => ({ label: o.name, value: o.id }))}
                        isSearchable={false}
                        classNames={reactSelectClassNames()}
                        onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                        menuPosition="fixed"
                        form="credit_payment_form"
                        styles={{ menu: ({ ...css }) => ({ ...css, width: 'max-content' }) }}
                      />
                    )}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <table className="table is-narrow table-payment-schedule-panel">
            <tbody>
              <tr>
                <td>Date</td>
                <td>
                  <input
                    name="payment_date"
                    className="input is-small"
                    type="date"
                    form="credit_payment_form"
                    {...register('payment_date', { required: true })}
                  />
                </td>
                <td colSpan={9}></td>
              </tr>

              <tr>
                <td colSpan={10}>Maximum Credit Amount: ${(responseData?.max_reimbursement).toFixed(2)}</td>
              </tr>

              <tr>
                <td>Amount $</td>
                <td>{totalAmount.toFixed(2)}</td>
                <td colSpan={9}></td>
              </tr>

              <tr>
                <td style={{ textAlign: 'left', padding: '4px 4px', width: '20%', verticalAlign: 'top' }}>
                  <span style={{ paddingRight: '2px' }}>Principal $</span>
                  {creditPaymentFields?.principalAmount && (
                    <span
                      className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                      data-tooltip={creditPaymentFields.principalAmount}
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                    </span>
                  )}
                </td>
                <td style={{ textAlign: 'left', padding: '2px 4px', verticalAlign: 'top' }}>
                  <Controller
                    name="principal_amount"
                    control={control}
                    defaultValue={0}
                    render={({ field: { onChange } }) => (
                      <input
                        name="principal_amount"
                        className="input is-small"
                        type="number"
                        form="credit_payment_form"
                        min={0}
                        max={responseData?.max_principal.toFixed(2)}
                        step="0.01"
                        {...register('principal_amount')}
                      />
                    )}
                  />
                </td>
                <td style={{ textAlign: 'left' }}>Max Amount: {responseData?.max_principal === 0 ? '0' : responseData?.max_principal.toFixed(2)}</td>
              </tr>
              <tr>
                <td style={{ textAlign: 'left', padding: '2px 4px', width: '20%', verticalAlign: 'top' }}>
                <span style={{ paddingRight: '2px' }}>Interest $</span>
                  {creditPaymentFields?.interestAmount && (
                    <span
                      className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                      data-tooltip={creditPaymentFields.interestAmount}
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                    </span>
                  )}
                </td>
                <td style={{ textAlign: 'left', padding: '2px 4px', verticalAlign: 'top' }}>
                  <Controller
                    name="interest_amount"
                    control={control}
                    defaultValue={0}
                    render={({ field: { onChange } }) => (
                      <input
                        name="interest_amount"
                        className="input is-small"
                        type="number"
                        form="credit_payment_form"
                        min={0}
                        max={responseData?.max_interest.toFixed(2)}
                        step="0.01"
                        {...register('interest_amount')}
                      />
                    )}
                  />
                </td>
                <td style={{ textAlign: 'left' }}>Max Amount: {responseData?.max_interest === 0 ? '0' : responseData?.max_interest.toFixed(2)}</td>
              </tr>
              <tr>
                <td style={{ textAlign: 'left', padding: '2px 4px', width: '20%', verticalAlign: 'top' }}>
                  <span style={{ paddingRight: '2px' }}>Late Fee $</span>
                  {creditPaymentFields?.lateFee && (
                    <span
                      className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                      data-tooltip={creditPaymentFields.lateFee}
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                    </span>
                  )}
                </td>
                <td style={{ textAlign: 'left', padding: '2px 4px', verticalAlign: 'top' }}>
                  <Controller
                    name="late_fee"
                    control={control}
                    defaultValue={0}
                    render={({ field: { onChange } }) => (
                      <input
                        name="late_fee"
                        className="input is-small"
                        type="number"
                        form="credit_payment_form"
                        min={0}
                        max={responseData?.max_late_fee.toFixed(2)}
                        step="0.01"
                        {...register('late_fee')}
                      />
                    )}
                  />
                </td>
                <td style={{ textAlign: 'left' }}>Max Amount: {responseData?.max_late_fee === 0 ? '0' : responseData?.max_late_fee.toFixed(2)}</td>
              </tr>
              <tr>
                <td style={{ textAlign: 'left', padding: '2px 4px', width: '20%', verticalAlign: 'top' }}>
                  <span style={{ paddingRight: '2px' }}>NSF Fee $</span>
                  {creditPaymentFields?.nsfFee && (
                    <span
                      className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                      data-tooltip={creditPaymentFields.nsfFee}
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                    </span>
                  )}
                </td>
                <td style={{ textAlign: 'left', padding: '2px 4px', verticalAlign: 'top' }}>
                  <Controller
                    name="nsf_fee"
                    control={control}
                    defaultValue={0}
                    render={({ field: { onChange } }) => (
                      <input
                        name="nsf_fee"
                        className="input is-small"
                        type="number"
                        form="credit_payment_form"
                        min={0}
                        max={responseData?.max_nsf_fee.toFixed(2)}
                        step="0.01"
                        {...register('nsf_fee')}
                      />
                    )}
                  />
                </td>
                <td style={{ textAlign: 'left' }}>Max Amount: {responseData?.max_nsf_fee === 0 ? '0' : responseData?.max_nsf_fee.toFixed(2)}</td>
              </tr>
              <tr>
                <td style={{ textAlign: 'left', padding: '2px 4px', width: '20%', verticalAlign: 'top' }}>
                  <span style={{ paddingRight: '2px' }}>Reimbursement</span>
                  {creditPaymentFields?.creditReimbursementAmount && (
                    <span
                      className={
                        'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'
                      }
                      data-tooltip={creditPaymentFields.creditReimbursementAmount}
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                    </span>
                  )}
                </td>
                <td style={{ textAlign: 'left', padding: '2px 4px', verticalAlign: 'top' }}>
                  <Controller
                    name="credit_reimbursement_amount"
                    control={control}
                    defaultValue={0}
                    render={({ field: { onChange } }) => (
                      <input
                        name="credit_reimbursement_amount"
                        className="input is-small"
                        type="number"
                        form="credit_payment_form"
                        min={0}
                        max={responseData?.max_reimbursement.toFixed(2)}
                        step="0.01"
                        {...register('credit_reimbursement_amount')}
                      />
                    )}
                  />
                </td>
                <td style={{ textAlign: 'left' }}>Max Amount: {responseData?.max_reimbursement === 0 ? '0' : responseData?.max_reimbursement.toFixed(2)}</td>
              </tr>
              <tr>
                <td colSpan={10}></td>
                <td>
                  <div className="has-text-right">
                    <button
                      type="button"
                      className="button has-background-grey has-text-white is-small mr-1"
                      onClick={() => checkChangesMade()}
                    >
                      <span className="mx-3">Cancel</span>
                    </button>
                    <button
                      type="submit"
                      className="button is-link is-small mr-1"
                      disabled={saving && 'disabled'}
                    >
                      <span className="mx-3">{saving ? 'Scheduling' : 'Schedule'}</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </form>
      )}
    </div>
  )
}
